import React, { useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  HStack,
  Spinner,
  Text,
  Select,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { CopyIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { QUERY_MATCHES, DELETE_MATCH, UPDATE_MATCH, GET_MATCH_RESULTS, DELETE_MATCH_RESULT } from '../../Queries';
import { useUser } from '../../Context';
import { Footer, WithSubnavigation } from '../../Components';

export const MatchManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUser();
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('6'); // Default to 6 months
  const [showAll, setShowAll] = useState(false);
  const [sortColumn, setSortColumn] = useState('date'); // Default sort by date
  const [sortDirection, setSortDirection] = useState('asc'); // Default ascending
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [matchToUpdate, setMatchToUpdate] = useState(null);
  const [matchToReset, setMatchToReset] = useState(null);
  const [matchToCancel, setMatchToCancel] = useState(null);
  const cancelRef = React.useRef();
  const resetCancelRef = React.useRef();
  const cancelMatchRef = React.useRef();

  // Get user email for filtering
  const userEmail = user?.attributes?.email;

  // Calculate date range for queries
  const { startDate, endDate } = React.useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    if (showAll) {
      // Query from 1 year ago to 5 years in future
      return {
        startDate: now - (365 * 24 * 60 * 60),
        endDate: now + (5 * 365 * 24 * 60 * 60)
      };
    }
    const monthsInSeconds = parseInt(timeRange) * 30 * 24 * 60 * 60;
    return {
      startDate: now - (30 * 24 * 60 * 60), // Include past month for in-progress/completed
      endDate: now + monthsInSeconds
    };
  }, [timeRange, showAll]);

  // Single query for all matches in date range
  const { data: allMatchesData, loading: allMatchesLoading } = useQuery(QUERY_MATCHES, {
    variables: {
      start_date: startDate,
      end_date: endDate,
    },
  });

  // Sort function
  const sortMatches = (matches) => {
    if (!matches || matches.length === 0) return matches;

    return [...matches].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = a.match_details.match_name?.toLowerCase() || '';
          bValue = b.match_details.match_name?.toLowerCase() || '';
          break;
        case 'date':
          aValue = a.match_details.match_date || 0;
          bValue = b.match_details.match_date || 0;
          break;
        case 'category':
          aValue = a.match_details.match_category?.toLowerCase() || '';
          bValue = b.match_details.match_category?.toLowerCase() || '';
          break;
        case 'discipline':
          aValue = a.match_details.match_discipline?.toLowerCase() || '';
          bValue = b.match_details.match_discipline?.toLowerCase() || '';
          break;
        case 'director':
          aValue = a.match_details.match_director?.toLowerCase() || '';
          bValue = b.match_details.match_director?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.match_details.match_status?.toLowerCase() || '';
          bValue = b.match_details.match_status?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Separate matches by status and apply sorting
  const allMatches = allMatchesData?.queryMatches || [];
  const upcomingMatches = sortMatches(allMatches.filter(m => m.match_details.match_status === 'UPCOMING'));
  const inProgressMatches = sortMatches(allMatches.filter(m => m.match_details.match_status === 'IN_PROGRESS'));
  const completedMatches = sortMatches(
    allMatches.filter(m => m.match_details.match_status === 'COMPLETED' || m.match_details.match_status === 'CANCELLED')
  );

  const filteredUpcoming = upcomingMatches;
  const filteredInProgress = inProgressMatches;
  const filteredCompleted = completedMatches;

  // Delete match mutation
  const [deleteMatch, { loading: deleting }] = useMutation(DELETE_MATCH, {
    onCompleted: () => {
      toast({
        title: 'Match deleted successfully',
        status: 'success',
        duration: 3000,
      });
      onClose();
      setMatchToDelete(null);
    },
    onError: (error) => {
      toast({
        title: 'Error deleting match',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    // Refetch all QueryMatches queries regardless of variables
    refetchQueries: ['QueryMatches'],
  });

  // Update match mutation (for status changes)
  const [updateMatch, { loading: updating }] = useMutation(UPDATE_MATCH, {
    onCompleted: () => {
      toast({
        title: 'Match status updated successfully',
        status: 'success',
        duration: 3000,
      });
      setMatchToUpdate(null);
    },
    onError: (error) => {
      toast({
        title: 'Error updating match status',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    // Refetch all QueryMatches queries regardless of variables
    refetchQueries: ['QueryMatches'],
  });

  // Delete match result mutation
  const [deleteMatchResult] = useMutation(DELETE_MATCH_RESULT, {
    onError: (error) => {
      console.error('Error deleting result:', error);
    },
  });

  const handleDeleteClick = (match) => {
    setMatchToDelete(match);
    onOpen();
  };

  const handleConfirmDelete = () => {
    if (matchToDelete) {
      deleteMatch({ variables: { match_id: matchToDelete.match_id } });
    }
  };

  const handleResetClick = (match) => {
    setMatchToReset(match);
    onResetOpen();
  };

  const handleCopyMatch = (match) => {
    // Navigate to create page with match data in state
    navigate('/matches/manage/new', {
      state: {
        copyFrom: {
          match_name: `${match.match_details.match_name} (Copy)`,
          match_date: match.match_details.match_date,
          match_category: match.match_details.match_category,
          match_discipline: match.match_details.match_discipline,
          match_location: match.match_details.match_location,
          match_director: match.match_details.match_director,
          match_director_email: match.match_details.match_director_email,
          match_format_id: match.match_details.match_format_id,
          registration_deadline: match.match_details.registration_deadline,
          max_participants: match.match_details.max_participants,
          match_description: match.match_details.match_description,
          match_status: 'UPCOMING', // Always create as upcoming
        }
      }
    });
  };

  const handleCancelClick = (match) => {
    setMatchToCancel(match);
    onCancelOpen();
  };

  const handleConfirmCancel = async () => {
    if (!matchToCancel) return;

    try {
      const details = matchToCancel.match_details;
      
      // Validate required fields
      if (!details.match_name) {
        throw new Error('Match name is required');
      }
      if (!details.match_date) {
        throw new Error('Match date is required');
      }
      if (!details.match_format_id) {
        throw new Error('Match format ID is required. This match may need to be edited to add a format.');
      }
      if (!details.match_director) {
        throw new Error('Match director is required');
      }
      
      // Build match details with only defined values
      const matchDetails = {
        match_name: details.match_name,
        match_date: details.match_date,
        match_format_id: details.match_format_id,
        match_status: 'CANCELLED',
        match_director: details.match_director,
      };

      // Add optional fields only if they exist
      if (details.match_category) matchDetails.match_category = details.match_category;
      if (details.match_discipline) matchDetails.match_discipline = details.match_discipline;
      if (details.match_location) matchDetails.match_location = details.match_location;
      if (details.match_director_email) matchDetails.match_director_email = details.match_director_email;
      if (details.registration_deadline) matchDetails.registration_deadline = details.registration_deadline;
      if (details.max_participants) matchDetails.max_participants = details.max_participants;
      if (details.match_description) matchDetails.match_description = details.match_description;
      if (details.weather_conditions) matchDetails.weather_conditions = details.weather_conditions;

      await updateMatch({
        variables: {
          match_id: matchToCancel.match_id,
          match_details: matchDetails,
        },
      });

      toast({
        title: 'Match cancelled successfully',
        description: 'Match has been moved to the Completed tab',
        status: 'success',
        duration: 5000,
      });

      onCancelClose();
      setMatchToCancel(null);
    } catch (error) {
      toast({
        title: 'Error cancelling match',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleConfirmReset = async () => {
    if (!matchToReset) return;

    try {
      // First, get all results for this match
      const { data } = await client.query({
        query: GET_MATCH_RESULTS,
        variables: { match_id: matchToReset.match_id },
        fetchPolicy: 'network-only',
      });

      const results = data?.getMatchResults || [];

      // Delete all results
      if (results.length > 0) {
        await Promise.all(
          results.map((result) =>
            deleteMatchResult({ variables: { result_id: result.result_id } })
          )
        );
      }

      // Update match status to UPCOMING
      const details = matchToReset.match_details;
      
      // Build match details with only defined values
      const matchDetails = {
        match_name: details.match_name,
        match_date: details.match_date,
        match_format_id: details.match_format_id,
        match_status: 'UPCOMING',
        match_director: details.match_director,
      };

      // Add optional fields only if they exist
      if (details.match_category) matchDetails.match_category = details.match_category;
      if (details.match_discipline) matchDetails.match_discipline = details.match_discipline;
      if (details.match_location) matchDetails.match_location = details.match_location;
      if (details.match_director_email) matchDetails.match_director_email = details.match_director_email;
      if (details.registration_deadline) matchDetails.registration_deadline = details.registration_deadline;
      if (details.max_participants) matchDetails.max_participants = details.max_participants;
      if (details.match_description) matchDetails.match_description = details.match_description;
      if (details.weather_conditions) matchDetails.weather_conditions = details.weather_conditions;

      await updateMatch({
        variables: {
          match_id: matchToReset.match_id,
          match_details: matchDetails,
        },
      });

      toast({
        title: 'Match reset successfully',
        description: `Deleted ${results.length} result(s) and reset match to UPCOMING status`,
        status: 'success',
        duration: 5000,
      });

      onResetClose();
      setMatchToReset(null);
    } catch (error) {
      toast({
        title: 'Error resetting match',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'UPCOMING':
        return 'blue';
      case 'IN_PROGRESS':
        return 'green';
      case 'COMPLETED':
        return 'gray';
      case 'CANCELLED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const SortableHeader = ({ column, label }) => {
    const isActive = sortColumn === column;
    return (
      <Th
        cursor="pointer"
        onClick={() => handleSort(column)}
        _hover={{ bg: 'gray.100' }}
        userSelect="none"
      >
        <Flex align="center" gap={1}>
          {label}
          {isActive && (
            <Icon
              as={sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon}
              boxSize={4}
            />
          )}
        </Flex>
      </Th>
    );
  };

  const MatchTable = ({ matches, loading, showResetButton = false, showCancelButton = false }) => {
    if (loading) {
      return (
        <Flex justify="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      );
    }

    if (!matches || matches.length === 0) {
      return (
        <Text p={4} color="gray.500">
          No matches found
        </Text>
      );
    }

    return (
      <Table variant="simple">
        <Thead>
          <Tr>
            <SortableHeader column="name" label="Match Name" />
            <SortableHeader column="date" label="Date" />
            <SortableHeader column="category" label="Category" />
            <SortableHeader column="discipline" label="Discipline" />
            <SortableHeader column="director" label="Match Director" />
            <SortableHeader column="status" label="Status" />
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {matches.map((match) => (
            <Tr key={match.match_id}>
              <Td fontWeight="medium">{match.match_details.match_name}</Td>
              <Td>{formatDate(match.match_details.match_date)}</Td>
              <Td>{match.match_details.match_category}</Td>
              <Td>{match.match_details.match_discipline}</Td>
              <Td>{match.match_details.match_director}</Td>
              <Td>
                <Badge colorScheme={getStatusColor(match.match_details.match_status)}>
                  {match.match_details.match_status}
                </Badge>
              </Td>
              <Td>
                <Flex gap={2} wrap="wrap">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/matches/manage/${match.match_id}/edit`)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => navigate(`/matches/manage/${match.match_id}/results`)}
                  >
                    Results
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<CopyIcon />}
                    colorScheme="green"
                    variant="outline"
                    onClick={() => handleCopyMatch(match)}
                  >
                    Copy
                  </Button>
                  {showCancelButton && (
                    <Button
                      size="sm"
                      colorScheme="yellow"
                      variant="outline"
                      onClick={() => handleCancelClick(match)}
                    >
                      Cancel Match
                    </Button>
                  )}
                  {showResetButton && (
                    <Button
                      size="sm"
                      colorScheme="orange"
                      variant="outline"
                      onClick={() => handleResetClick(match)}
                    >
                      Reset to Upcoming
                    </Button>
                  )}
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteClick(match)}
                  >
                    Delete
                  </Button>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <Box bg="#F7F7F7" minH="100vh">
      <WithSubnavigation />
      <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg">Match Management</Heading>
          <Flex gap={3}>
            <Button
              variant="outline"
              onClick={() => navigate('/matches/manage/formats')}
            >
              Manage Formats
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => navigate('/matches/manage/new')}
            >
              Create New Match
            </Button>
          </Flex>
        </Flex>

        {/* Instructions Box */}
        <Box mb={6} p={6} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
          <Heading size="sm" mb={3} color="blue.800">
            Match Management Workflow
          </Heading>
          <VStack align="stretch" spacing={2} fontSize="sm" color="gray.700">
            <Text>
              <strong>1. Create a Match Format:</strong> Click "Manage Formats" → "Create Format". Define your match structure with stages, scoring types (ring scoring, time-based, etc.), divisions, and categories. Save as a template for reuse.
            </Text>
            <Text>
              <strong>2. Set Up a Match:</strong> Click "Create New Match". Enter match details (name, date, location, director), and choose your format. The match starts with UPCOMING status.
            </Text>
            <Text>
              <strong>3. Start the Match:</strong> On match day, click "Results" for your match, then click "Start Match" to change status to IN_PROGRESS. This enables score entry.
            </Text>
            <Text>
              <strong>4. Enter Results:</strong> Add participants one at a time with their name, email, division, and category. Enter scores for each stage - aggregate scores calculate automatically. Results are staged locally until you save them.
            </Text>
            <Text>
              <strong>5. Save & Finalize:</strong> Click "Save Results" to save staged entries without completing the match, or "Finalize Match" to save all results, mark the match COMPLETED, and generate a printable report.
            </Text>
            <Text>
              <strong>6. Post-Match Edits:</strong> If corrections are needed after finalization, click "Reopen Match" to return to IN_PROGRESS status. Make your edits, then finalize again.
            </Text>
            <Text fontSize="xs" color="gray.600" mt={2}>
              Tip: Results are ranked automatically by division and category. Stage-by-stage scoring ensures accurate results and proper tiebreaker handling.
            </Text>
          </VStack>
        </Box>

        <HStack justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
          <Heading size="md">Match List</Heading>
          <HStack spacing={2} flexWrap="wrap">
            <Text fontSize="sm" color="gray.600">Show matches for:</Text>
            <Select 
              size="sm" 
              width="auto" 
              value={showAll ? 'all' : timeRange}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setShowAll(true);
                } else {
                  setShowAll(false);
                  setTimeRange(e.target.value);
                }
              }}
            >
              <option value="3">Next 3 months</option>
              <option value="6">Next 6 months</option>
              <option value="12">Next 12 months</option>
              <option value="all">All upcoming</option>
            </Select>
          </HStack>
        </HStack>

        <Tabs index={activeTab} onChange={setActiveTab}>
          <TabList>
            <Tab>Upcoming</Tab>
            <Tab>In Progress</Tab>
            <Tab>Completed</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <MatchTable
                matches={filteredUpcoming}
                loading={allMatchesLoading}
                showCancelButton={true}
              />
            </TabPanel>
            <TabPanel>
              <MatchTable
                matches={filteredInProgress}
                loading={allMatchesLoading}
                showResetButton={true}
                showCancelButton={true}
              />
            </TabPanel>
            <TabPanel>
              <MatchTable
                matches={filteredCompleted}
                loading={allMatchesLoading}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Match
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{matchToDelete?.match_details?.match_name}"?
              This action cannot be undone. All associated results will also be deleted.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleConfirmDelete} 
                ml={3} 
                isLoading={deleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog
        isOpen={isResetOpen}
        leastDestructiveRef={resetCancelRef}
        onClose={onResetClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reset Match to Upcoming
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to reset "{matchToReset?.match_details?.match_name}" back to UPCOMING status?
              <br />
              <br />
              <strong>This will delete all existing scores and results for this match.</strong>
              <br />
              <br />
              This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={resetCancelRef} onClick={onResetClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="orange" 
                onClick={handleConfirmReset} 
                ml={3}
              >
                Reset Match
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Cancel Match Confirmation Dialog */}
      <AlertDialog
        isOpen={isCancelOpen}
        leastDestructiveRef={cancelMatchRef}
        onClose={onCancelClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel Match
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to cancel "{matchToCancel?.match_details?.match_name}"?
              <br />
              <br />
              The match will be marked as CANCELLED and moved to the Completed tab. Members will see it as cancelled on the schedule.
              <br />
              <br />
              You can still edit or delete the match later if needed.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelMatchRef} onClick={onCancelClose}>
                Don't Cancel
              </Button>
              <Button 
                colorScheme="yellow" 
                onClick={handleConfirmCancel} 
                ml={3}
              >
                Cancel Match
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
